// Small helpers for Rostery Mail's screens.
export const OUTLOOK_BLUE = '#0f6cbd';

const AVATAR = ['#0f6cbd', '#8764b8', '#038387', '#ca5010', '#c239b3', '#498205', '#986f0b', '#e43ba6', '#4f6bed', '#d13438'];
export function avatarColor(seed) {
  let h = 0;
  for (const ch of String(seed || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR[h % AVATAR.length];
}
export function initials(name, address) {
  const n = String(name || '').trim();
  if (n) {
    const parts = n.replace(/[^\p{L}\p{N} ]/gu, ' ').split(/\s+/).filter(Boolean);
    return ((parts[0] || '')[0] || '').toUpperCase() + ((parts.length > 1 ? parts[parts.length - 1][0] : '') || '').toUpperCase();
  }
  return String(address || '?').slice(0, 2).toUpperCase();
}
export const who = (a) => (a ? a.name || a.address : '');
export const firstOf = (list) => (Array.isArray(list) && list.length ? list[0] : null);

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
// Outlook's list time: 10:42 am today, Mon 10:42 am this week, 14/10/2026 older.
export function listTime(iso) {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  if (sameDay(d, now)) return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  const days = (now - d) / 86400000;
  if (days < 7) return d.toLocaleDateString('en-AU', { weekday: 'short' }) + ' ' + d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
export function fullTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
// Groups for the message list: Today, Yesterday, This week, Last week, This month, Older.
export function groupOf(iso) {
  if (!iso) return 'Older';
  const d = new Date(iso); const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = 86400000;
  if (d >= start) return 'Today';
  if (d >= new Date(start - day)) return 'Yesterday';
  const weekStart = new Date(start - ((start.getDay() + 6) % 7) * day);
  if (d >= weekStart) return 'This week';
  if (d >= new Date(weekStart - 7 * day)) return 'Last week';
  if (d >= new Date(now.getFullYear(), now.getMonth(), 1)) return 'This month';
  return 'Older';
}
export const fmtSize = (n) => (!n ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// The address list as typed text and back.
export const addrText = (list) => (list || []).map((a) => (a.name ? `${a.name} <${a.address}>` : a.address)).join(', ');
export const escapeHtml = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const stripPrefixes = (s) => { let t = String(s || '').trim(); for (let i = 0; i < 10; i++) { const m = /^(re|fw|fwd)\s*:\s*/i.exec(t); if (!m) break; t = t.slice(m[0].length); } return t; };
export const replySubject = (s) => `RE: ${stripPrefixes(s)}`;
export const forwardSubject = (s) => `FW: ${stripPrefixes(s)}`;

export function replyTo(msg, me, all) {
  const mine = new Set([String(me || '').toLowerCase()]);
  const notMe = (a) => !mine.has(String(a.address).toLowerCase());
  const base = ((msg.reply_to && msg.reply_to.length) ? msg.reply_to : msg.from || []).filter(notMe);
  if (!all) return { to: base, cc: [] };
  const seen = new Set(base.map((a) => a.address));
  const to = [...base];
  for (const a of msg.to || []) if (notMe(a) && !seen.has(a.address)) { to.push(a); seen.add(a.address); }
  const cc = (msg.cc || []).filter((a) => notMe(a) && !seen.has(a.address));
  return { to, cc };
}
// A meeting invite's times as instants. A local time with a time zone (TZID) is read in that zone;
// a zone the browser does not know (Windows names) is read as the browser's own.
function zonedToDate(local, tz) {
  const [d, t = '00:00'] = String(local).split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  const own = new Date(y, mo - 1, da, h || 0, mi || 0);
  if (!tz) return own;
  try {
    const guess = Date.UTC(y, mo - 1, da, h || 0, mi || 0);
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    const p = Object.fromEntries(fmt.formatToParts(new Date(guess)).map((x) => [x.type, x.value]));
    const asZone = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute);
    return new Date(guess - (asZone - guess));
  } catch { return own; }
}
export function inviteTimes(inv) {
  if (!inv) return null;
  const at = (k) => (inv[`${k}_utc`] ? new Date(inv[`${k}_utc`]) : inv[`${k}_local`] ? zonedToDate(inv[`${k}_local`], inv.all_day ? null : inv.tzid) : null);
  const start = at('start');
  if (!start || Number.isNaN(start.getTime())) return null;
  let end = at('end');
  if (!end || Number.isNaN(end.getTime()) || end <= start) end = new Date(start.getTime() + (inv.all_day ? 86400000 : 3600000));
  return { start, end, all_day: !!inv.all_day };
}
export function inviteWhen(t) {
  if (!t) return '';
  const day = (d) => d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const time = (d) => d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  if (t.all_day) {
    const last = new Date(t.end.getTime() - 86400000);
    return last > t.start ? `${day(t.start)} to ${day(last)} (all day)` : `${day(t.start)} (all day)`;
  }
  const sameDay = t.start.toDateString() === t.end.toDateString();
  return sameDay ? `${day(t.start)}, ${time(t.start)} to ${time(t.end)}` : `${day(t.start)}, ${time(t.start)} to ${day(t.end)}, ${time(t.end)}`;
}

// The quoted original under a reply or forward, as Outlook writes it.
export function quoteBlock(msg) {
  const list = (x) => (x || []).map((a) => escapeHtml(a.name ? `${a.name} <${a.address}>` : a.address)).join('; ');
  const head = [`<b>From:</b> ${list(msg.from)}`, `<b>Sent:</b> ${escapeHtml(fullTime(msg.date))}`, `<b>To:</b> ${list(msg.to)}`,
    (msg.cc || []).length ? `<b>Cc:</b> ${list(msg.cc)}` : '', `<b>Subject:</b> ${escapeHtml(msg.subject || '')}`].filter(Boolean).join('<br>');
  const body = msg.html || `<pre style="white-space:pre-wrap;font-family:inherit">${escapeHtml(msg.text || '')}</pre>`;
  return `<p></p><hr style="border:none;border-top:1px solid #e1e1e1"><p style="font-size:13px;color:#444">${head}</p><blockquote style="margin:0;padding-left:12px;border-left:3px solid #e1e1e1">${body}</blockquote>`;
}
